import { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import './VotePage.css';
import voteIconPNG from '../images/quick-link-vote-icon.png';
import runelocusPNG from '../images/vote/runelocus.png';
import rspsListPNG from '../images/vote/rsps-list.png';
import voteInfoSVG from '../images/vote/vote-info.svg';
import ReasonNav from '../components/reason/ReasonNav';
import ReasonFooter from '../components/reason/ReasonFooter';
import { Link } from '../components/Link';

function decodeUsername(raw) {
  if (!raw) return '';
  return decodeURIComponent(raw).replace(/[-_]/g, ' ').trim().toLowerCase();
}

function toUrlUsername(name) {
  return name.trim().toLowerCase().replace(/ /g, '-');
}

const VOTE_SITE_IMAGES = {
  runelocus: runelocusPNG,
  rspslist: rspsListPNG,
};

function getVoteSiteImage(title) {
  const key = title.toLowerCase().replace(/[^a-z0-9]/g, '');
  return VOTE_SITE_IMAGES[key] || voteIconPNG;
}

function currentMonthLabel() {
  const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return labels[new Date().getUTCMonth()];
}

function formatCooldownLabel(remainingSeconds) {
  const hours = Math.max(1, Math.ceil(remainingSeconds / 3600));
  if (hours === 1) {
    return 'Vote in 1 hour from now';
  }
  return `Vote in ${hours} hours from now`;
}

function getSiteStatus(voteStatus, siteId) {
  if (!voteStatus?.sites) return null;
  return voteStatus.sites.find((site) => String(site.siteId) === String(siteId)) || null;
}

function VoteLeaderboard({ voters, month }) {
  return (
    <div className="main-container">
      <p className="secondary-title" style={{ marginBottom: '20px' }}>
        Top 10 Voters ({month || currentMonthLabel()})
      </p>
      {voters.length === 0 ? (
        <p className="vote-instructions mb-0">No votes recorded this month yet.</p>
      ) : (
        voters.map((voter) => (
          <div key={voter.rank} className="top-vote-container">
            <div className="top-vote-rank">
              {voter.rank}. {voter.username}
            </div>
            <div className="top-vote-amount">{voter.voteCount}</div>
          </div>
        ))
      )}
    </div>
  );
}

function VoteInstructions() {
  return (
    <div className="main-container vote-sites-col">
      <p className="vote-instructions mb-0">
        <img src={voteInfoSVG} alt="vote info icon" />
        Type <span className="voted-cmd">::voted</span> ingame to get your vote rewards
      </p>
    </div>
  );
}

function VotePage() {
  const { username: urlUsername } = useParams();
  const history = useHistory();
  const [links, setLinks] = useState([]);
  const [topVoters, setTopVoters] = useState([]);
  const [leaderboardMonth, setLeaderboardMonth] = useState(currentMonthLabel);
  const [inputName, setInputName] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [voteStatus, setVoteStatus] = useState({ sites: [] });
  const displayName = decodeUsername(urlUsername);

  useEffect(() => {
    const root = document.getElementById('root');
    const prevRootBg = root?.style.backgroundColor;
    if (root) root.style.backgroundColor = '#111013';
    return () => {
      document.body.style.overflow = '';
      if (root) root.style.backgroundColor = prevRootBg || '';
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
  }, [menuOpen]);

  useEffect(() => {
    fetch('/api/vote/links')
      .then((res) => res.json())
      .then((data) => setLinks(Array.isArray(data) ? data : []))
      .catch(() => setLinks([]));

    fetch('/api/vote/leaderboard')
      .then((res) => res.json())
      .then((data) => {
        setTopVoters(Array.isArray(data.voters) ? data.voters : []);
        setLeaderboardMonth(data.month || currentMonthLabel());
      })
      .catch(() => {
        setTopVoters([]);
        setLeaderboardMonth(currentMonthLabel());
      });
  }, []);

  useEffect(() => {
    if (!displayName) {
      setVoteStatus({ sites: [] });
      return;
    }

    fetch(`/api/vote/status?username=${encodeURIComponent(displayName)}`)
      .then((res) => res.json())
      .then((data) => {
        setVoteStatus({
          cooldownSeconds: data.cooldownSeconds,
          sites: Array.isArray(data.sites) ? data.sites : [],
        });
      })
      .catch(() => setVoteStatus({ sites: [] }));
  }, [displayName]);

  function handleLookup(e) {
    e.preventDefault();
    const trimmed = inputName.trim();
    if (trimmed) {
      history.push(`/vote/${toUrlUsername(trimmed)}`);
    }
  }

  function buildVoteUrl(template) {
    const name = displayName || inputName.trim();
    const urlName = toUrlUsername(name);
    return template.replace(/\{username\}/g, encodeURIComponent(urlName));
  }

  return (
    <div className={`reason-page vote-page${menuOpen ? ' menu-open' : ''}`}>
      <ReasonNav activePage="vote" menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="container mx-auto vote-body">
        <h2>Vote</h2>
      </div>

      {!displayName && (
        <UsernameLookup
          inputName={inputName}
          setInputName={setInputName}
          onSubmit={handleLookup}
        />
      )}
      {displayName && (
        <VoteSites
          displayName={displayName}
          links={links}
          buildVoteUrl={buildVoteUrl}
          voteStatus={voteStatus}
          topVoters={topVoters}
          leaderboardMonth={leaderboardMonth}
        />
      )}

      <ReasonFooter />
    </div>
  );
}

function UsernameLookup({ inputName, setInputName, onSubmit }) {
  return (
    <div className="container main-container vote-page-container">
      <p className="header-text">Enter Username To Vote</p>
      <div className="row">
        <div className="col-md-4" />
        <div className="col-md-4">
          <form className="vote-form-group" onSubmit={onSubmit}>
            <label htmlFor="username">Please enter your username to continue</label>
            <input
              id="username"
              type="text"
              className="form-text form-control mb-3"
              placeholder="username..."
              maxLength={16}
              value={inputName}
              onChange={(e) => setInputName(e.target.value)}
            />
            <button type="submit" className="button">
              Continue
            </button>
          </form>
        </div>
        <div className="col-md-4" />
      </div>
    </div>
  );
}

function VoteSiteButton({ link, voteStatus, buildVoteUrl }) {
  const siteStatus = getSiteStatus(voteStatus, link.site_id);
  const onCooldown = siteStatus && !siteStatus.available && siteStatus.remainingSeconds > 0;

  if (onCooldown) {
    return (
      <span className="button third vote-cooldown">
        {formatCooldownLabel(siteStatus.remainingSeconds)}
        <span title="You have claimed this"> ✔</span>
      </span>
    );
  }

  return (
    <a
      className="button"
      href={buildVoteUrl(link.url)}
      target="_blank"
      rel="noreferrer"
    >
      Vote Now
    </a>
  );
}

function VoteSites({ displayName, links, buildVoteUrl, voteStatus, topVoters, leaderboardMonth }) {
  return (
    <div className="container mx-auto vote-content-container">
      <div className="row">
        <div className="col-md-8 vote-sites-col">
          <div className="main-container" style={{ marginRight: '20px', marginBottom: '40px' }}>
            <div className="voting-container-top">
              <div className="voting-title">
                <p className="secondary-title">Start Voting</p>
              </div>
              <div className="voting-info">
                <span>Voting for {displayName}</span>
                <Link to="/vote">Change</Link>
              </div>
            </div>

            <div className="row">
              {links.length === 0 ? (
                <div className="col-md-12">
                  <p className="vote-instructions text-center mb-0">
                    No vote sites configured yet. Check back soon.
                  </p>
                </div>
              ) : (
                links.map((link) => (
                  <div key={link.id} className="col-md-12">
                    <div className="main-container vote-site-card">
                      <div className="vote-item">
                        <img
                          src={getVoteSiteImage(link.title)}
                          alt={link.title}
                        />
                        <div className="vote-button">
                          <VoteSiteButton
                            link={link}
                            voteStatus={voteStatus}
                            buildVoteUrl={buildVoteUrl}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <VoteInstructions />
        </div>

        <div className="col-md-4 vote-leaderboard-col">
          <VoteLeaderboard voters={topVoters} month={leaderboardMonth} />
        </div>
      </div>
    </div>
  );
}

export default VotePage;
